/**
 * NIVARO cart — <nv-cart-upsell>
 *
 * Lazily loads Shopify product recommendations for the cart (complementary first,
 * related as fallback) through the Section Rendering API and injects the cards the
 * section renders inside `[data-nv-upsell-response]`. Stays hidden (CSS) when the
 * response has no cards: sold-out or already-in-cart products are filtered in Liquid.
 *
 * The element carries `data-skip-subtree-update`, so Horizon's cart morph keeps the
 * loaded cards on quantity changes; its id encodes the cart's products, so the morph
 * replaces it (and it re-fetches) whenever the products in the cart change.
 */
class NvCartUpsell extends HTMLElement {
  connectedCallback() {
    if (this.#started || !this.dataset.url) return;
    this.#started = true;
    if (!('IntersectionObserver' in window)) {
      this.#load();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: '300px 0px' }
    );
    observer.observe(this);
  }

  #started = false;

  async #fetchCards(intent) {
    const response = await fetch(`${this.dataset.url}&intent=${intent}`, { headers: { Accept: 'text/html' } });
    if (!response.ok) return null;
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const wrapper = doc.querySelector('[data-nv-upsell-response]');
    return wrapper && wrapper.children.length ? wrapper : null;
  }

  async #load() {
    const slot = this.querySelector('[data-nv-upsell-slot]');
    if (!slot) return;
    try {
      const wrapper = (await this.#fetchCards('complementary')) || (await this.#fetchCards('related'));
      if (!wrapper || !this.isConnected) return;
      slot.replaceChildren(...Array.from(wrapper.children));
    } catch (error) {
      console.warn('[nivaro] cart recommendations unavailable', error);
    }
  }
}

if (!customElements.get('nv-cart-upsell')) customElements.define('nv-cart-upsell', NvCartUpsell);
